import React, { Fragment } from 'react';
import { FormattedMessage as FormattedMessageOriginal } from 'react-intl';
import ReactPaginate from 'react-paginate';
import Filter from '@common-components/filter/Filter';
import Table from '@common-components/table/Table';
import ConditionalRendering from '@components/common/conditional-rendering/ConditionalRendering';
import AlertMessage from '../individualPortal/dashboard/AlertMsgs/AlertMessage';
import { ALL_USER_ROLES, USER_GROUPS } from '@constants/roles/userRoleTypes';
import { DELEGATION_HEADER_TYPES, labelNames, filterInputsMaxLength, DELEGATION_ALERT_MSGS } from '@constants/delegation/delegationConfig';
import { INoteProps, IDelegationViewProps } from './delegationTypes';

// Type assertion to resolve react-intl type conflicts with React types
const FormattedMessage = FormattedMessageOriginal as React.ComponentType<any>;

// Constants
const ITEMS_PER_PAGE = 10;

// Note paragraph available only for CSR group and Consumer.
const Note: React.FC<INoteProps> = ({ roleGroup }) => {
    if (roleGroup !== USER_GROUPS.CSR_GROUP && roleGroup !== ALL_USER_ROLES.INDIVIDUAL) {
        return null;
    }

    return (
        <div className="delegation__note">
            <p>
                <FormattedMessage 
                    id="delegation_note_text"
                    values={{ 
                        note: <b><FormattedMessage id="delegation_note"/></b>, 
                        strong: (chunks: React.ReactNode[]) => <strong>{chunks}</strong>
                    } as Record<string, any>}
                />
            </p>
        </div>
    );
};

const DelegationView: React.FC<IDelegationViewProps> = (props) => {
    const {
        roleGroup,
        filters,
        spinner,
        pagination,
        tableData,
        tableColumns,
        onChangeHandler,
        onResetHandler,
        onApplyHandler,
        pageNumber,
        onPageNumberChange,
        resolvedLabelNames,
        resolvedFilterInputsMaxLength
    } = props;

    const alertMessageId = [ALL_USER_ROLES.BROKER, ALL_USER_ROLES.ASSISTER].includes(roleGroup)
        ? DELEGATION_ALERT_MSGS.CASE_ID
        : DELEGATION_ALERT_MSGS.DEFAULT;

    return (
        <Fragment>
            <h2 className="delegation__header"><FormattedMessage id={DELEGATION_HEADER_TYPES[roleGroup]}/></h2>
            <div className="gi-panel">
                <h4 className="delegation__filter-header"><FormattedMessage id="delegation_filter"/></h4>
                <Note roleGroup={roleGroup}/>

                {filters && Object.keys(filters).length > 0 && (
                    <Filter
                        filterList={filters}
                        filterInputsMaxLength={resolvedFilterInputsMaxLength ?? filterInputsMaxLength}
                        labelNames={resolvedLabelNames ?? labelNames}
                        formName="filterForm"
                        handleChange={onChangeHandler}
                        resetHandler={onResetHandler}
                        applyHandler={onApplyHandler}
                    />
                )}
            </div>
            <div className="gi-panel">
                <ConditionalRendering isTrue={spinner && spinner.activeSpinnersQty > 0}>
                    <FormattedMessage id="spinner.loading_data" tagName="div" />
                </ConditionalRendering>
                <ConditionalRendering isTrue={!spinner?.activeSpinnersQty && tableData && tableData.length > 0}>
                    <Table
                        data={tableData}
                        columns={tableColumns}
                        className="delegation-history-table"
                    />
                </ConditionalRendering>
                <ConditionalRendering isTrue={!spinner?.activeSpinnersQty && (!tableData || tableData.length === 0)}>
                    <AlertMessage
                        content={
                            <FormattedMessage id={alertMessageId} />
                        }
                        type="info"
                        showIcon={false}
                    />
                </ConditionalRendering>
            </div>
            {pagination && pagination.count > ITEMS_PER_PAGE && (
                <ReactPaginate
                    previousLabel={
                        <FormattedMessage id="delegation_pagination_prev"/>
                    }
                    nextLabel={
                        <FormattedMessage id="delegation_pagination_next"/>
                    }
                    breakLabel="..."
                    breakClassName="break-me"
                    previousClassName="pagination_prev"
                    nextClassName="pagination_next"
                    pageCount={Math.ceil(pagination.count / ITEMS_PER_PAGE)}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={onPageNumberChange}
                    containerClassName="pagination"
                    activeClassName="active"
                    forcePage={pageNumber}
                    hrefBuilder={() => '#'}
                />
            )}
        </Fragment>
    );
};

export default DelegationView;
